import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router-dom";
import {
  ApiError,
  ago,
  api,
  atLeast,
  on,
  type Bench,
  type LaunchTarget,
  type Project,
  type Role,
  type Run,
} from "../api";
import KnownProblems from "../components/KnownProblems";
import LaunchPanel from "../components/LaunchPanel";
import { Board, Loading, Notice, PageHead, Row, Section, Status, type Tone } from "../components/Look";
import Modal from "../components/Modal";
import StartTesting from "../components/StartTesting";
import { buildVerdict, failedOf, type ProjectBuild } from "../components/buildState";
import { useTitle } from "../useTitle";
import { Route as CampaignRoute } from "./Builds";
import { rigState } from "./Now";

/**
 * A product's Overview: is its newest build good, what needs a look, and the
 * benches. The first page after choosing a product (step K3, 2026-09-28).
 *
 * The answer comes from the newest BUILD - its newest campaign, else its
 * newest full run. A product whose runs record no build yet (SNCF, IPI-6 and
 * io-core until step K0) is answered from its newest full run, and the page
 * says why rather than inventing a build.
 */
export default function ProductOverview() {
  const { t } = useTranslation();
  const pid = Number(useParams().id);
  const [project, setProject] = useState<Project | null>(null);
  const [benches, setBenches] = useState<Bench[]>([]);
  const [role, setRole] = useState<Role | null>(null);
  const [launch, setLaunch] = useState<LaunchTarget | undefined>(undefined);
  const [builds, setBuilds] = useState<ProjectBuild[] | null>(null);
  const [unbuilt, setUnbuilt] = useState<Run[]>([]);
  const [error, setError] = useState("");
  const [dialog, setDialog] = useState<"" | "start" | "launch">("");
  useTitle(project ? project.name : t("projects.fallbackTitle", { id: pid }));

  const load = useCallback(async () => {
    try {
      const d = await api.project(pid);
      setProject(d.project);
      setBenches(d.benches);
      setRole(d.role ?? null);
      setLaunch(d.launch);
      setError("");
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    }
    try {
      const b = await api.projectBuilds(pid);
      setBuilds(b.builds);
      setUnbuilt(b.unbuilt);
    } catch {
      /* a service from before builds: the page answers from nothing */
      setBuilds([]);
    }
  }, [pid]);
  useEffect(() => {
    void load();
    const timer = setInterval(() => void load(), 60_000);
    return () => clearInterval(timer);
  }, [load]);

  if (error) return <Notice tone="error">{error}</Notice>;
  if (!project || builds === null) return <Loading label={t("common.loading")} />;

  const newest = builds[0];
  const verdict = newest ? buildVerdict(newest) : null;
  const camp = newest?.campaigns[0];
  const loose = unbuilt.find((r) => r.scope === "full") ?? unbuilt[0];
  const mayRun = atLeast(role, "operator") && benches.some((b) => b.has_agent !== false);

  let answer: string;
  let bad = false;
  let lead: React.ReactNode = null;
  if (newest && verdict) {
    answer = `${newest.label || newest.key}: ${verdict.answer}`;
    bad = verdict.tone === "bad";
    lead = camp
      ? t("overview.leadCampaign", { id: camp.id, when: on(camp.created_at), ran: camp.ran, planned: camp.planned ?? "?" })
      : t("overview.leadBuildRuns", { runs: newest.runs.length, when: on(newest.last_at) });
  } else if (loose) {
    const f = failedOf(loose);
    answer = f ? t("builds.aRunFailed", { run: loose.name, count: f }) : t("builds.aRunClean", { run: loose.name });
    bad = f > 0;
    lead = (
      <>
        {t("overview.leadRun", { when: on(loose.started_at), bench: loose.bench_name })}
        {loose.reason ? ` · ${loose.reason}` : ""}
        <br />
        <span className="text-sm text-slate-500">{t("overview.noBuild")}</span>
      </>
    );
  } else {
    answer = t("overview.nothing");
  }

  // What needs a look, from what this page already holds. Each item says
  // what happened and links to where it is fixed.
  const attention: { tone: Tone; word: string; text: React.ReactNode; to: string; link: string }[] = [];
  for (const s of camp?.stops ?? []) {
    if (s.lost && s.run_id) {
      attention.push({ tone: "bad", word: t("overview.aLost"), text: t("overview.aLostText", { run: s.run_name, stop: s.label || s.profile }), to: `/runs/${s.run_id}`, link: t("overview.open") });
    }
  }
  const now = Date.now() / 1000;
  for (const b of benches) {
    if (b.has_agent !== false && !(b.agent_alive ?? b.online) && b.last_seen_at && now - b.last_seen_at > 86_400) {
      attention.push({ tone: "warn", word: t("overview.aQuiet"), text: t("overview.aQuietText", { bench: b.name, when: ago(b.last_seen_at) }), to: `/benches/${b.id}`, link: t("overview.bench") });
    }
  }

  return (
    <section>
      <PageHead
        title={project.name}
        answer={answer}
        bad={bad}
        after={lead ? <p>{lead}</p> : undefined}
        actions={
          <>
            {mayRun && (
              <button type="button" className="btn-primary" onClick={() => setDialog("start")}>
                {t("overview.start")}
              </button>
            )}
            {newest && (
              <Link className="btn-quiet" to={`/projects/${pid}/builds/${encodeURIComponent(newest.key)}`}>
                {t("overview.openBuild", { build: newest.label || newest.key })}
              </Link>
            )}
            {launch && atLeast(role, "operator") && (
              <button type="button" className="btn-quiet" onClick={() => setDialog("launch")}>
                {t("overview.launch")}
              </button>
            )}
          </>
        }
      />

      {!attention.length && (
        <p className="mt-2 text-base" data-testid="attention-none">
          <Status tone="good">{t("overview.attentionNone")}</Status>
        </p>
      )}

      {camp && (
        <Section
          card
          title={t("overview.newestCampaign", { build: newest?.label || newest?.key })}
          actions={<Link className="btn-quiet" to={`/campaigns/${camp.id}`}>{t("overview.openCampaign")}</Link>}
        >
          <CampaignRoute stops={camp.stops} />
        </Section>
      )}

      <div className={`grid gap-x-6 ${attention.length ? "lg:grid-cols-2 lg:items-start" : ""}`}>
        {attention.length > 0 && (
        <Section card title={t("overview.attention")}>
            <Board as="ul">
              {attention.map((a, i) => (
                <li key={i}>
                  <Row
                    status={<Status tone={a.tone}>{a.word}</Status>}
                    title={a.text}
                    aside={<Link className="text-sm underline" to={a.to}>{a.link}</Link>}
                  />
                </li>
              ))}
            </Board>
        </Section>
        )}

        <Section
          card
          title={t("overview.benches", { count: benches.length })}
          actions={<Link className="btn-quiet" to={`/benches?project=${pid}`}>{t("overview.allBenches")}</Link>}
        >
          {benches.length ? (
            <Board as="ul">
              {benches.map((b) => {
                const st = rigState(b);
                const tone: Tone = st.tone === "up" ? (b.lease ? "warn" : "good") : "off";
                return (
                  <li key={b.id}>
                    <Row
                      status={<Status tone={tone} title={st.why}>{st.text}</Status>}
                      title={<Link className="font-semibold hover:underline" to={`/benches/${b.id}`}>{b.name}</Link>}
                      sub={b.lease ? t("overview.heldBy", { actor: b.lease.actor, purpose: b.lease.purpose || "" }) : b.description || undefined}
                      aside={<span className="whitespace-nowrap text-sm text-slate-500">{t("projects.lastSeen", { when: ago(b.last_seen_at) })}</span>}
                    />
                  </li>
                );
              })}
            </Board>
          ) : (
            <p className="text-base text-slate-500">{t("projects.noBenches")}</p>
          )}
        </Section>
      </div>

      <KnownProblems projectId={pid} role={role} />

      <p className="mt-8 text-sm text-slate-500">
        <Link className="underline" to={`/projects/${pid}/settings`}>{t("overview.settings")}</Link>
      </p>

      {dialog === "start" && (
        <Modal wide title={t("overview.start")} onClose={() => setDialog("")}>
          <StartTesting projectId={pid} fromProject />
        </Modal>
      )}
      {dialog === "launch" && (
        <Modal title={t("overview.launch")} onClose={() => setDialog("")}>
          <LaunchPanel projectId={pid} launch={launch} role={role} />
        </Modal>
      )}
    </section>
  );
}
